import { components } from "@certn/api/generated-schemas/client-portal";
import type { components as ApplicantPortalComponents } from "@certn/api/generated-schemas/applicant-portal";
import type { components as CaseManagementPortalComponents } from "@certn/api/generated-schemas/case-management";

export type Score = components["schemas"]["CheckDataScoreEnum"];

export type AppIdentifier =
  | "CLIENT_PORTAL"
  | "APPLICANT_PORTAL"
  | "CASE_MANAGEMENT";

export type SubScore = components["schemas"]["CheckDataSubScoreEnum"];

export const getSubScoreLabel = (
  t: (key: string, defaultValue?: string) => string,
  subScore: SubScore
): string =>
  ({
    NEGATIVE: t("Negative"),
    POSITIVE: t("Positive"),
    VERIFIED: t("Verified"),
    RESULTS_FOUND: t("Results found"),
    NO_MATCH_FOUND: t("No match found"),
    PARTIALLY_VERIFIED: t("Partially verified"),
    UNABLE_TO_VERIFY: t("Unable to verify"),
    UNABLE_TO_COMPLETE: t("Unable to complete"),
    DISCREPANCY: t("Discrepancy"),
    NO_DISCREPANCY: t("No discrepancy"),
    REVIEW: t("Review"),
    CLEAR: t("SCORE Clear", "Clear"),
    NOT_VERIFIED: t("Not verified"),
    NOT_APPLICABLE: t("Not applicable"),
    RESTRICTED: t("Restricted"),
    REJECT: t("Reject"),
  })[subScore];

export type CheckResult = {
  id: string;
  short_id: string;
  type: string;
  type_display: string;
  status: string;
  status_display: string;
  score: Score | null | "";
  score_display: string | null;
  sub_score: SubScore | null | "";
  sub_score_display: string | null;
  completed_at: string | null;
  heading_messages: string[] | null;
  claims?: { [key: string]: unknown };
  supplementary_information?: { [key: string]: unknown };
  results: {
    claims?: { [key: string]: unknown };
    supplementary_information?: { [key: string]: unknown };
  };
  results_data_schema: { [key: string]: unknown };
  results_ui_schema: { [key: string]: unknown };
  child_checks?: CheckResult[];
};

export type ReportAttachmentEsign =
  components["schemas"]["ReportAttachmentEsign"];

export type ReportAttachment = components["schemas"]["ReportAttachment"];

export type SummaryOfRightsEsignData = ReportAttachmentEsign;

export type ReportData = {
  case_details: {
    case_id: string;
    short_id: string;
    date_ordered: string;
    cancellation_reason?: string | null;
    needs_nyc_article_23a_notice?: boolean | undefined;
  };
  applicant_info: {
    email: string;
    current_name: components["schemas"]["CurrentName"];
    current_location: components["schemas"]["CurrentResidence"];
    date_of_birth: string;
  };
  date_completed: string | null;
  date_generated?: string | null;
  checks: CheckResult[];
  report_pdf_logo_url?: string | null;
  exclude_overview?: boolean;
  report_attachments?: ReportAttachment[];
};

export type FulfillmentNotes = components["schemas"]["ReportFulfillmentNotes"];
export type AllChecksFulfillmentNotes =
  components["schemas"]["GetCaseReportCase"]["fulfillment_notes"];

export type CaseReportFilePollResult =
  | components["schemas"]["RetrieveCaseReportFile"]
  | ApplicantPortalComponents["schemas"]["CaseReportFile"]
  | CaseManagementPortalComponents["schemas"]["CaseManagementRetrieveCaseReportFile"];

/** Presigned URL may still be returned when `authenticatedCaseReportPdfDownload` is off. */
export type CaseReportFilePollResponse = CaseReportFilePollResult & {
  pdf_url?: string | null;
};

export type CaseReportGenerateResponse =
  | components["schemas"]["CaseReportResponse"]
  | ApplicantPortalComponents["schemas"]["CaseReportResponse"]
  | CaseManagementPortalComponents["schemas"]["CaseReportResponse"];
